import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft, ArrowRight, ChevronLeft, Fingerprint, KeyRound, Mail, ShieldCheck } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { useAuth } from "@/_core/hooks/useAuth";
import { resolvePostLoginPath, toSafeAppPath } from "@shared/authRouting";
import { trpc } from "@/lib/trpc";

export default function LoginPage() {
  const [, navigate] = useLocation();
  const { user, loading, error, isAuthenticated } = useAuth();
  const utils = trpc.useUtils();
  const returnTo = useMemo(() => {
    if (typeof window === "undefined") return undefined;
    return toSafeAppPath(new URLSearchParams(window.location.search).get("returnTo"));
  }, []);
  const initialEmail = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("email")?.trim().toLowerCase() ?? "";
  }, []);
  const createdAccountMessage = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("created") === "1"
      ? "Your profile is ready. Enter your email to receive a secure sign-in code."
      : "";
  }, []);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpChallengeId, setTotpChallengeId] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [stage, setStage] = useState<"email" | "code" | "totp">("email");
  const [formError, setFormError] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  /** Address we just confirmed has no account, so the notice can name it. */
  const [noAccountEmail, setNoAccountEmail] = useState("");
  /**
   * Whether the passcode in the field came back from the server rather than an
   * email, which happens only under the local development gate in
   * server/localOtpPrefill.ts. Drives the banner that says so.
   */
  const [codePrefilledLocally, setCodePrefilledLocally] = useState(false);
  const [statusMessage, setStatusMessage] = useState(createdAccountMessage);
  const requestOtp = trpc.auth.requestOtp.useMutation();
  const verifyOtp = trpc.auth.verifyOtp.useMutation();
  const passkeyAuthOptions = trpc.auth.passkey.authOptions.useMutation();
  const passkeyAuthVerify = trpc.auth.passkey.authVerify.useMutation();
  const verifyTotp = trpc.auth.totp.verifyLogin.useMutation();
  const verifyRecoveryCode = trpc.auth.totp.verifyRecoveryCode.useMutation();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    navigate(resolvePostLoginPath(user.role, returnTo));
  }, [isAuthenticated, navigate, returnTo, user]);

  function handleEmailChange(value: string) {
    setEmail(value);
    // The notice names one address; the moment it is edited the notice is stale.
    if (noAccountEmail) setNoAccountEmail("");
  }

  async function requestCode() {
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setFormError("Enter the email address associated with your account.");
      return;
    }

    setFormError("");
    setStatusMessage("");
    try {
      const result = await requestOtp.mutateAsync({ email: normalizedEmail });

      // No account for that address: say so, and keep them on this step with the
      // address still in the field so a typo is a one-character fix rather than a
      // retype. Staying put matters more than the wording — advancing to the code
      // screen would ask them to wait for a code that is not coming.
      if (!result.accepted) {
        setEmail(normalizedEmail);
        setNoAccountEmail(normalizedEmail);
        return;
      }

      setNoAccountEmail("");
      setEmail(normalizedEmail);

      /*
       * Undefined in every environment but local development, where it saves a trip
       * through the mailbox that has no mail provider behind it.
       */
      const prefill = "localPrefillCode" in result ? result.localPrefillCode : undefined;
      if (prefill) {
        setCode(prefill);
        setCodePrefilledLocally(true);
        setStatusMessage("Development sign-in is enabled. Signing you in now…");
        const verification = await verifyOtp.mutateAsync({
          email: normalizedEmail,
          code: prefill,
          returnTo,
          trustDevice,
        });
        if (verification.requiresTotp) {
          setTotpChallengeId(verification.challengeId);
          setTotpCode("");
          setUseRecoveryCode(false);
          setStage("totp");
          setStatusMessage("Development email verification is complete. Enter the current code from your authenticator app.");
          return;
        }
        await utils.auth.me.invalidate();
        navigate(verification.destination);
        return;
      }
      setCode(prefill ?? "");
      setCodePrefilledLocally(Boolean(prefill));
      setStage("code");
      setStatusMessage(
        prefill
          ? "A six-digit code was issued and filled in below. It expires in 10 minutes."
          : `A six-digit code is on its way to ${normalizedEmail}. It expires in 10 minutes.`,
      );
    } catch (requestError) {
      setFormError(requestError instanceof Error ? requestError.message : "We could not send a secure code. Please try again.");
    }
  }

  async function handleRequestCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await requestCode();
  }

  async function handleVerifyCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedCode = code.replace(/\D/g, "").slice(0, 6);
    if (normalizedCode.length !== 6) {
      setFormError("Enter the six-digit code from your email.");
      return;
    }

    setFormError("");
    setStatusMessage("");
    try {
      const result = await verifyOtp.mutateAsync({
        email: email.trim().toLowerCase(),
        code: normalizedCode,
        returnTo,
        trustDevice,
      });
      if (result.requiresTotp) {
        setTotpChallengeId(result.challengeId);
        setTotpCode("");
        setUseRecoveryCode(false);
        setStage("totp");
        setStatusMessage("Email verification is complete. Enter the current code from your authenticator app to finish signing in.");
        return;
      }
      await utils.auth.me.invalidate();
      navigate(result.destination);
    } catch (verificationError) {
      setFormError(verificationError instanceof Error ? verificationError.message : "We could not verify that code. Please request a new one and try again.");
    }
  }

  async function handlePasskeySignIn() {
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setFormError("Enter the email address associated with your account before using a passkey.");
      return;
    }
    if (typeof window === "undefined" || !window.isSecureContext || !("PublicKeyCredential" in window)) {
      setFormError("Passkeys require a current browser on a secure HTTPS connection. Use your email sign-in code instead.");
      return;
    }

    setFormError("");
    setStatusMessage("Use the secure prompt from your device to continue.");
    try {
      const { ceremonyId, options } = await passkeyAuthOptions.mutateAsync({ email: normalizedEmail });
      const response = await startAuthentication({ optionsJSON: options });
      const result = await passkeyAuthVerify.mutateAsync({
        email: normalizedEmail,
        ceremonyId,
        response,
        returnTo,
        trustDevice,
      });
      if (result.requiresTotp) {
        setTotpChallengeId(result.challengeId);
        setTotpCode("");
        setUseRecoveryCode(false);
        setStage("totp");
        setStatusMessage("Passkey verification is complete. Enter the current code from your authenticator app to finish signing in.");
        return;
      }
      await utils.auth.me.invalidate();
      navigate(result.destination);
    } catch (passkeyError) {
      const message = passkeyError instanceof Error ? passkeyError.message : "";
      setStatusMessage("");
      setFormError(
        /notallowed|cancelled|canceled/i.test(message)
          ? "Passkey sign-in was cancelled. You can try again or use an email sign-in code."
          : message || "We could not verify that passkey. Try again or use your email sign-in code.",
      );
    }
  }

  function returnToEmailStep() {
    setStage("email");
    setCode("");
    setCodePrefilledLocally(false);
    setTotpCode("");
    setTotpChallengeId("");
    setUseRecoveryCode(false);
    setFormError("");
    setStatusMessage("");
  }

  async function handleTotpVerification(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!totpChallengeId) {
      returnToEmailStep();
      setFormError("This authenticator verification request has expired. Start sign-in again.");
      return;
    }

    const candidate = useRecoveryCode ? totpCode.trim() : totpCode.replace(/\D/g, "").slice(0, 6);
    if (useRecoveryCode ? !candidate : candidate.length !== 6) {
      setFormError(useRecoveryCode ? "Enter one of your recovery codes." : "Enter the six-digit code from your authenticator app.");
      return;
    }

    setFormError("");
    setStatusMessage("");
    try {
      const result = useRecoveryCode
        ? await verifyRecoveryCode.mutateAsync({ challengeId: totpChallengeId, recoveryCode: candidate })
        : await verifyTotp.mutateAsync({ challengeId: totpChallengeId, code: candidate });
      await utils.auth.me.invalidate();
      navigate(result.destination);
    } catch (verificationError) {
      setFormError(verificationError instanceof Error ? verificationError.message : "We could not verify that authenticator code. Start again and try a current code.");
    }
  }

  const isWorking = requestOtp.isPending || verifyOtp.isPending || passkeyAuthOptions.isPending || passkeyAuthVerify.isPending || verifyTotp.isPending || verifyRecoveryCode.isPending;

  return (
    <main
      style={{
        minHeight: "100vh",
        background: "#080C18",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)", width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle, rgba(233,30,140,0.07) 0%, transparent 65%)" }} />
        <div style={{ position: "absolute", bottom: "-15%", right: "-10%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 65%)" }} />
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
      </div>

      {/*
        Back to the site, above the card rather than buried under it.

        Matched to the card's 440px so it sits flush with the card's left edge —
        a back affordance floating in the centre reads as another action in the
        flow, while one aligned to the top-left corner reads as an exit from it.
      */}
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 440, marginBottom: "1.15rem" }}>
        <Link
          href="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.45rem",
            marginLeft: "-0.35rem",
            padding: "0.35rem",
            borderRadius: "0.5rem",
            color: "#FFFFFF",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "1.05rem",
            fontWeight: 700,
            letterSpacing: "-0.01em",
            textDecoration: "none",
          }}
        >
          <ChevronLeft size={20} aria-hidden="true" />
          Return to site
        </Link>
      </div>

      <section
        aria-labelledby="login-title"
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 440,
          background: "rgba(13,18,32,0.92)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "1.25rem",
          padding: "clamp(1.5rem, 6vw, 2.5rem)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(233,30,140,0.06)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          textAlign: "center",
        }}
      >
        <p style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", margin: "0 0 .8rem", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }}>
          <ShieldCheck size={14} aria-hidden="true" /> Secure account access
        </p>
        <h1 id="login-title" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 7vw, 2.45rem)", fontWeight: 700, color: "#FFF", letterSpacing: ".04em", margin: "0 0 .55rem" }}>
          Welcome to the Alliance
        </h1>
        <p style={{ margin: "0 auto", maxWidth: 330, color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.65 }}>
          {stage === "email"
            ? "Enter your email to receive a secure six-digit code. Administrators go to the CRM; members go to their events dashboard."
            : stage === "code"
              ? "Enter the six-digit code sent to your email. It expires in 10 minutes and can be used only once."
              : "Confirm your authenticator app to finish signing in. You can use a saved recovery code if your app is unavailable."}
        </p>

        {/*
          The address the visitor typed, shown back to them on the code step.
          Without it there is nothing on screen to check a typo against, and the
          only feedback for a wrong address is a code that never arrives.
          Rendered in Inter because an email address has to be read character by
          character, and Inter is a family the page actually loads.
        */}
        {stage === "code" && (
          <p
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: ".5rem",
              flexWrap: "wrap",
              margin: "1.1rem auto 0",
              padding: ".62rem .85rem",
              maxWidth: 330,
              borderRadius: ".6rem",
              background: "rgba(255,255,255,.04)",
              border: "1px solid rgba(255,255,255,.1)",
              fontFamily: "'Inter', sans-serif",
              fontSize: "var(--chi-body-compact)",
              lineHeight: 1.5,
              color: "#8E9AAF",
            }}
          >
            <Mail size={14} aria-hidden="true" style={{ color: "#7DEAFF", flexShrink: 0 }} />
            <span>Email address</span>
            <strong style={{ color: "#FFF", fontWeight: 650, overflowWrap: "anywhere" }}>{email}</strong>
          </p>
        )}

        {stage === "email" ? (
          <form onSubmit={handleRequestCode} noValidate>
            <label htmlFor="member-email" style={{ display: "block", marginTop: "1.45rem", textAlign: "left", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".75rem", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
              Email address
            </label>
            <div style={{ position: "relative", marginTop: ".5rem" }}>
              <Mail size={17} aria-hidden="true" style={{ position: "absolute", left: ".9rem", top: "50%", transform: "translateY(-50%)", color: "#7DEAFF" }} />
              <input
                id="member-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={event => handleEmailChange(event.target.value)}
                aria-invalid={Boolean(formError)}
                aria-describedby={formError ? "member-email-error" : undefined}
                placeholder="Enter your email address"
                style={{ width: "100%", minHeight: 48, padding: ".8rem .9rem .8rem 2.8rem", color: "#FFF", background: "rgba(255,255,255,.045)", border: formError ? "1px solid rgba(244,63,94,.75)" : "1px solid rgba(255,255,255,.13)", borderRadius: ".65rem", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: ".92rem" }}
              />
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: ".65rem", marginTop: "1rem", padding: ".72rem .8rem", borderRadius: ".65rem", background: "rgba(125,234,255,.045)", border: "1px solid rgba(125,234,255,.16)", color: "#BFDCE4", textAlign: "left", fontFamily: "'Inter', sans-serif", fontSize: ".76rem", lineHeight: 1.45, cursor: isWorking ? "wait" : "pointer" }}>
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={event => setTrustDevice(event.target.checked)}
                disabled={isWorking}
                style={{ width: 16, height: 16, marginTop: 1, accentColor: "#7DEAFF", flexShrink: 0 }}
              />
              <span><strong style={{ color: "#E8FBFF" }}>Trust this device for 7 days.</strong> You can sign out at any time to revoke this remembered session.</span>
            </label>
            <button
              type="submit"
              disabled={loading || isWorking}
              style={{ marginTop: "1.25rem", width: "100%", minHeight: 48, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: ".55rem", padding: ".9rem 1.25rem", background: loading || isWorking ? "rgba(233,30,140,.48)" : "linear-gradient(135deg, #E91E8C 0%, #C2185B 100%)", border: 0, borderRadius: ".65rem", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".92rem", fontWeight: 750, cursor: loading || isWorking ? "wait" : "pointer", boxShadow: loading || isWorking ? "none" : "0 4px 20px rgba(233,30,140,.35)", transition: "transform 160ms cubic-bezier(.23,1,.32,1), box-shadow 160ms cubic-bezier(.23,1,.32,1)" }}
            >
              <Mail size={17} aria-hidden="true" />
              {requestOtp.isPending ? "Sending secure code…" : "Email me a secure code"}
              {!requestOtp.isPending && <ArrowRight size={15} aria-hidden="true" />}
            </button>
            <div aria-hidden="true" style={{ display: "flex", alignItems: "center", gap: ".7rem", margin: "1rem 0 .8rem", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: ".68rem" }}>
              <span style={{ height: 1, flex: 1, background: "rgba(255,255,255,.1)" }} />
              or
              <span style={{ height: 1, flex: 1, background: "rgba(255,255,255,.1)" }} />
            </div>
            <button
              type="button"
              onClick={() => void handlePasskeySignIn()}
              disabled={loading || isWorking}
              style={{ width: "100%", minHeight: 46, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: ".55rem", padding: ".8rem 1.1rem", background: "rgba(125,234,255,.08)", border: "1px solid rgba(125,234,255,.36)", borderRadius: ".65rem", color: "#CFF7FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".86rem", fontWeight: 700, cursor: loading || isWorking ? "wait" : "pointer" }}
            >
              <Fingerprint size={17} aria-hidden="true" />
              {passkeyAuthOptions.isPending || passkeyAuthVerify.isPending ? "Waiting for your passkey…" : "Sign in with a passkey"}
            </button>
            <p style={{ margin: ".55rem 0 0", color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
              Use Face ID, Touch ID, your device PIN, or a security key.
            </p>
          </form>
        ) : stage === "code" ? (
          <form onSubmit={handleVerifyCode} noValidate>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".75rem", marginTop: "1.45rem" }}>
              <label htmlFor="member-code" style={{ textAlign: "left", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".75rem", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
                Six-digit code
              </label>
              <button type="button" onClick={returnToEmailStep} disabled={isWorking} style={{ border: 0, background: "transparent", color: "#F9A8D4", fontFamily: "'Inter', sans-serif", fontSize: ".76rem", cursor: isWorking ? "wait" : "pointer", textDecoration: "underline" }}>
                Use another email
              </button>
            </div>
            <div style={{ position: "relative", marginTop: ".5rem" }}>
              <KeyRound size={17} aria-hidden="true" style={{ position: "absolute", left: ".9rem", top: "50%", transform: "translateY(-50%)", color: "#7DEAFF" }} />
              <input
                id="member-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                aria-invalid={Boolean(formError)}
                aria-describedby={formError ? "member-code-error" : undefined}
                placeholder="Enter the 6-digit code"
                style={{ width: "100%", minHeight: 52, padding: ".8rem .9rem .8rem 2.8rem", color: "#FFF", background: "rgba(255,255,255,.045)", border: formError ? "1px solid rgba(244,63,94,.75)" : "1px solid rgba(255,255,255,.13)", borderRadius: ".65rem", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: "1.25rem", fontWeight: 700, letterSpacing: ".32em" }}
              />
            </div>
            {/*
              Says plainly where the code came from. A passcode appearing on its own
              is the sort of thing that should never be ambiguous about which
              environment you are looking at.
            */}
            {codePrefilledLocally && (
              <p role="status" style={{ margin: ".55rem 0 0", padding: ".5rem .65rem", borderRadius: ".5rem", background: "rgba(245,166,35,.1)", border: "1px dashed rgba(245,166,35,.45)", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 650, textAlign: "left", lineHeight: 1.5 }}>
                Local development only — DEV_PREFILL_OTP is on, so this code came back from the server instead of an email.
              </p>
            )}
            <label style={{ display: "flex", alignItems: "flex-start", gap: ".65rem", marginTop: "1rem", padding: ".72rem .8rem", borderRadius: ".65rem", background: "rgba(125,234,255,.045)", border: "1px solid rgba(125,234,255,.16)", color: "#BFDCE4", textAlign: "left", fontFamily: "'Inter', sans-serif", fontSize: ".76rem", lineHeight: 1.45, cursor: isWorking ? "wait" : "pointer" }}>
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={event => setTrustDevice(event.target.checked)}
                disabled={isWorking}
                style={{ width: 16, height: 16, marginTop: 1, accentColor: "#7DEAFF", flexShrink: 0 }}
              />
              <span><strong style={{ color: "#E8FBFF" }}>Trust this device for 7 days.</strong> You can sign out at any time to revoke this remembered session.</span>
            </label>
            <button
              type="submit"
              disabled={isWorking}
              style={{ marginTop: "1.25rem", width: "100%", minHeight: 48, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: ".55rem", padding: ".9rem 1.25rem", background: isWorking ? "rgba(233,30,140,.48)" : "linear-gradient(135deg, #E91E8C 0%, #C2185B 100%)", border: 0, borderRadius: ".65rem", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".92rem", fontWeight: 750, cursor: isWorking ? "wait" : "pointer", boxShadow: isWorking ? "none" : "0 4px 20px rgba(233,30,140,.35)", transition: "transform 160ms cubic-bezier(.23,1,.32,1), box-shadow 160ms cubic-bezier(.23,1,.32,1)" }}
            >
              <KeyRound size={17} aria-hidden="true" />
              {verifyOtp.isPending ? "Verifying secure code…" : "Verify and continue"}
              {!verifyOtp.isPending && <ArrowRight size={15} aria-hidden="true" />}
            </button>
            <button type="button" disabled={isWorking} onClick={() => void requestCode()} style={{ marginTop: ".8rem", width: "100%", minHeight: 40, border: "1px solid rgba(255,255,255,.13)", borderRadius: ".6rem", background: "rgba(255,255,255,.025)", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".8rem", fontWeight: 650, cursor: isWorking ? "wait" : "pointer" }}>
              {requestOtp.isPending ? "Sending a new code…" : "Send a new code"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleTotpVerification} noValidate>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".75rem", marginTop: "1.45rem" }}>
              <label htmlFor="member-totp-code" style={{ textAlign: "left", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".75rem", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
                {useRecoveryCode ? "Recovery code" : "Authenticator code"}
              </label>
              <button type="button" onClick={returnToEmailStep} disabled={isWorking} style={{ border: 0, background: "transparent", color: "#F9A8D4", fontFamily: "'Inter', sans-serif", fontSize: ".76rem", cursor: isWorking ? "wait" : "pointer", textDecoration: "underline" }}>
                Start over
              </button>
            </div>
            <p style={{ margin: ".55rem 0 0", color: "#91A2B8", textAlign: "left", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
              {useRecoveryCode
                ? "Each recovery code works once. Use a remaining saved code exactly as shown."
                : "Open your authenticator app and enter its current six-digit code."}
            </p>
            <div style={{ position: "relative", marginTop: ".85rem" }}>
              <ShieldCheck size={17} aria-hidden="true" style={{ position: "absolute", left: ".9rem", top: "50%", transform: "translateY(-50%)", color: "#7DEAFF" }} />
              <input
                id="member-totp-code"
                name={useRecoveryCode ? "recovery-code" : "totp-code"}
                type="text"
                inputMode={useRecoveryCode ? "text" : "numeric"}
                autoComplete={useRecoveryCode ? "off" : "one-time-code"}
                pattern={useRecoveryCode ? "[A-Za-z0-9 -]+" : "[0-9]{6}"}
                maxLength={useRecoveryCode ? 32 : 6}
                required
                value={totpCode}
                onChange={event => setTotpCode(useRecoveryCode ? event.target.value.toUpperCase() : event.target.value.replace(/\D/g, "").slice(0, 6))}
                aria-invalid={Boolean(formError)}
                aria-describedby={formError ? "member-totp-error" : undefined}
                placeholder={useRecoveryCode ? "Enter one of your recovery codes" : "Enter the 6-digit code"}
                style={{ width: "100%", minHeight: 52, padding: ".8rem .9rem .8rem 2.8rem", color: "#FFF", background: "rgba(255,255,255,.045)", border: formError ? "1px solid rgba(244,63,94,.75)" : "1px solid rgba(255,255,255,.13)", borderRadius: ".65rem", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: useRecoveryCode ? ".98rem" : "1.25rem", fontWeight: 700, letterSpacing: useRecoveryCode ? ".08em" : ".32em" }}
              />
            </div>
            <button
              type="submit"
              disabled={isWorking}
              style={{ marginTop: "1.25rem", width: "100%", minHeight: 48, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: ".55rem", padding: ".9rem 1.25rem", background: isWorking ? "rgba(233,30,140,.48)" : "linear-gradient(135deg, #E91E8C 0%, #C2185B 100%)", border: 0, borderRadius: ".65rem", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".92rem", fontWeight: 750, cursor: isWorking ? "wait" : "pointer", boxShadow: isWorking ? "none" : "0 4px 20px rgba(233,30,140,.35)" }}
            >
              <ShieldCheck size={17} aria-hidden="true" />
              {verifyTotp.isPending || verifyRecoveryCode.isPending ? "Verifying authenticator…" : "Verify and sign in"}
              {!verifyTotp.isPending && !verifyRecoveryCode.isPending && <ArrowRight size={15} aria-hidden="true" />}
            </button>
            <button
              type="button"
              disabled={isWorking}
              onClick={() => { setUseRecoveryCode(value => !value); setTotpCode(""); setFormError(""); }}
              style={{ marginTop: ".8rem", width: "100%", minHeight: 40, border: "1px solid rgba(125,234,255,.24)", borderRadius: ".6rem", background: "rgba(125,234,255,.04)", color: "#CFF7FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".8rem", fontWeight: 650, cursor: isWorking ? "wait" : "pointer" }}
            >
              {useRecoveryCode ? "Use my authenticator app" : "Use a recovery code instead"}
            </button>
          </form>
        )}

        {formError && (
          <p id={stage === "email" ? "member-email-error" : stage === "code" ? "member-code-error" : "member-totp-error"} role="alert" style={{ margin: ".7rem 0 0", textAlign: "left", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
            {formError}
          </p>
        )}
        {/*
          No account for the address they entered.

          Copywriting choices worth keeping: say what happened in plain words
          ("we couldn't find"), name the address so a typo is visible, offer both
          recoveries — fix the spelling or create an account — and blame nobody.
          Avoided: "invalid" and "valid", which read as an accusation and describe
          the data rather than the situation; "that information", which is vaguer
          than the address already on screen; and any dead end without a next step.
        */}
        {noAccountEmail && (
          <div role="status" aria-live="polite" style={{ margin: ".85rem 0 0", padding: ".8rem .9rem", borderRadius: ".6rem", textAlign: "left", color: "#FDE68A", background: "rgba(245,166,35,.08)", border: "1px solid rgba(245,166,35,.28)", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
            We couldn&rsquo;t find an account for <strong style={{ color: "#FFF" }}>{noAccountEmail}</strong>. Check the spelling, or{" "}
            <Link href={`/create-account?email=${encodeURIComponent(noAccountEmail)}`} style={{ color: "#F9A8D4", fontWeight: 700, textDecoration: "none" }}>
              create your account
            </Link>
            .
          </div>
        )}
        {statusMessage && <p role="status" aria-live="polite" style={{ margin: ".85rem 0 0", padding: ".72rem .85rem", borderRadius: ".6rem", color: "#BAE6FD", background: "rgba(14,165,233,.08)", border: "1px solid rgba(14,165,233,.22)", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>{statusMessage}</p>}
        {error && !loading && <p role="alert" style={{ margin: "1.1rem 0 0", padding: ".72rem .85rem", borderRadius: ".6rem", color: "#FDA4AF", background: "rgba(244,63,94,.08)", border: "1px solid rgba(244,63,94,.25)", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>We could not confirm your account status. Please try again.</p>}

        <p style={{ margin: "1.2rem 0 0", paddingBottom: "1rem", color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
          New to The CHI Alliance? <Link href="/create-account" style={{ color: "#F9A8D4", fontWeight: 700, textDecoration: "none" }}>Create your profile</Link>
        </p>
        <hr style={{ borderColor: "rgb(255 166 241 / 21%)" }} />
        <div style={{ display: "flex", justifyContent: "center", paddingTop: "1rem" }}>
          <img src="/media/brand/chi-alliance-logo.png" alt="The CHI Alliance" style={{ height: 70, width: "auto", objectFit: "contain" }} />
        </div>
      </section>
    </main>
  );
}
